/**
 * PluginRuntime — ponte estática entre o app e os plugins/PluginWrap.
 *
 * O app (host) injeta uma vez o contexto da sessão já autenticada (socket
 * conectado, companyId, user) via `PluginRuntime.configure(...)`. Os plugins e
 * o PluginWrap consomem sem conhecer AuthContext nem abrir socket próprio —
 * reusam o socket autenticado do app (nada público).
 *
 * Mesmo padrão de `setModalOpener` (o host injeta a capability, o plugin usa).
 */

import { setPluginDebug, isPluginDebug } from "./debug";

type SocketLike = {
  on(event: string, handler: (...args: any[]) => void): void;
  off(event: string, handler: (...args: any[]) => void): void;
} | null;

interface RuntimeConfig {
  socket?: SocketLike;
  companyId?: number | null;
  user?: any;
  /** Config/preferências do usuário logado. */
  userSettings?: any;
  /** Conexões (whatsApps) da empresa. */
  connections?: any[];
  /** Config da empresa. */
  companySettings?: any;
  /** React/ReactDOM do app — pra plugins montarem componentes React reais. */
  react?: any;
  reactDOM?: any;
  /** Quaisquer outros dados de sessão que o app queira expor (via get). */
  [k: string]: any;
}

/** Eventos que o app pode emitir e os plugins assinar (frentes eventos + push). */
export type RuntimeEvent =
  | "ticket:message"        // mensagem nova num ticket
  | "ticket:updated"        // ticket alterado (status, fila, atribuição…)
  | "contact:updated"
  | "connection:status"
  | "active-ticket:change"  // o ticket aberto no chat mudou (store reativo)
  | string;                 // extensível

/** Ações de alto nível que SÓ o front sabe executar (registradas pelo app). */
export interface RuntimeActions {
  /** Abre/foca um ticket na UI. */
  openTicket?: (ticketId: number | string) => void;
  /** Navega para uma rota do app. */
  navigate?: (path: string) => void;
  /** Pede refresh de alguma área da UI (ex: "tickets", "contacts"). */
  refresh?: (area?: string) => void;
  /** Extensível: o app pode registrar outras ações. */
  [name: string]: ((...args: any[]) => any) | undefined;
}

/**
 * Store central do contexto da sessão, exposto aos plugins. O app alimenta via
 * configure()/setActiveTicket(); plugins leem (PluginRuntime.user, .companyId,
 * .activeTicket, .get("x")). Evita cada plugin/slot puxar de AuthContext e
 * centraliza o que pode ser reusado (usuário, empresa, ticket aberto, config).
 */
class PluginRuntimeClass {
  private socket: SocketLike = null;
  companyId: number | null = null;
  user: any = null;
  userSettings: any = null;
  /** Ticket atualmente aberto no chat (setado pelo app ao abrir). */
  activeTicket: any = null;
  /** Conexões (whatsApps) e config da empresa (alimentadas via configure). */
  connections: any[] = [];
  companySettings: any = null;
  /** React/ReactDOM DO APP — permitem ao plugin montar componentes React reais
   *  (mesma instância, sem bundle próprio). Acesse via PluginRuntime.react. */
  react: any = null;
  reactDOM: any = null;
  /** Ações de alto nível registradas pelo app (frente híbrida). */
  actions: RuntimeActions = {};
  /** Saco genérico para dados extras de sessão. */
  private store: Record<string, any> = {};
  /** Event bus: nome do evento → listeners. */
  private listeners: Map<string, Set<(payload: any) => void>> = new Map();

  /** O app injeta/atualiza o contexto da sessão (idempotente, merge raso). */
  configure(cfg: RuntimeConfig): void {
    const known = ["socket", "companyId", "user", "userSettings", "connections", "companySettings", "react", "reactDOM"];
    if ("socket" in cfg) this.socket = cfg.socket || null;
    if ("companyId" in cfg) this.companyId = cfg.companyId ?? null;
    if ("user" in cfg) this.user = cfg.user ?? null;
    if ("userSettings" in cfg) this.userSettings = cfg.userSettings ?? null;
    if ("connections" in cfg) this.connections = cfg.connections || [];
    if ("companySettings" in cfg) this.companySettings = cfg.companySettings ?? null;
    if ("react" in cfg) this.react = cfg.react || null;
    if ("reactDOM" in cfg) this.reactDOM = cfg.reactDOM || null;
    for (const k of Object.keys(cfg)) {
      if (!known.includes(k)) this.store[k] = cfg[k];
    }
  }

  /** O app registra ações de alto nível (merge — pode chamar várias vezes). */
  registerActions(actions: RuntimeActions): void {
    this.actions = { ...this.actions, ...actions };
  }

  /** Define o ticket aberto no chat; emite "active-ticket:change". */
  setActiveTicket(ticket: any): void {
    this.activeTicket = ticket || null;
    this.emit("active-ticket:change", this.activeTicket);
  }

  /** Lê um dado extra do store por chave. */
  get(key: string): any { return this.store[key]; }

  // ── Event bus ──────────────────────────────────────────────────────────

  /** Assina um evento do runtime. Retorna unsubscribe. */
  on(event: RuntimeEvent, cb: (payload: any) => void): () => void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    const set = this.listeners.get(event)!;
    set.add(cb);
    return () => { set.delete(cb); };
  }

  /** O app (host) emite um evento para os plugins. */
  emit(event: RuntimeEvent, payload?: any): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const cb of set) { try { cb(payload); } catch { /* noop */ } }
  }

  /**
   * Atalho: assina mensagens novas de um ticket específico. Reusa o socket
   * autenticado do app via o evento "ticket:message" (emitido pelo host quando
   * chega company-{companyId}-appMessage). Retorna unsubscribe.
   */
  onTicketMessage(ticketId: number | string, cb: () => void): () => void {
    return this.on("ticket:message", (payload: any) => {
      const tId = payload && (payload.ticketId ?? payload.ticket?.id ?? payload.message?.ticketId);
      if (String(tId) === String(ticketId)) {
        try { cb(); } catch { /* noop */ }
      }
    });
  }

  /** Liga/desliga os logs de debug do sistema de plugins (persiste). */
  setDebug(on: boolean): void { setPluginDebug(on); }

  /** Estado atual do debug. */
  isDebug(): boolean { return isPluginDebug(); }
}

/** Singleton compartilhado (app configura, plugins consomem). */
export const PluginRuntime = new PluginRuntimeClass();

// Expõe no global para acesso via console do navegador:
//   window.markedeskRuntime.setDebug(true)
try {
  if (typeof globalThis !== "undefined") (globalThis as any).markedeskRuntime = PluginRuntime;
} catch { /* noop */ }
